import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type SidekickConfig,
  SidekickConfigContext,
} from '../contexts/SidekickConfigContext';
import { SIDEKICK_TRANSLATION_NAMESPACE } from '../translations';
import { resolveBrowserTimeZone } from '../utils/timeZone';

/**
 * Reads the Sidekick locale + timezone configuration. Works both
 * inside and outside `SidekickConfigProvider`:
 *
 * - Inside a provider, returns the value supplied by the host.
 * - Outside a provider, returns `{ timeZone, locale }` derived from
 *   the browser (`Intl.DateTimeFormat`) and the active i18next
 *   language. This keeps the package usable by hosts that haven't
 *   wrapped their tree yet.
 *
 * @returns The active {@link SidekickConfig}.
 */
export const useSidekickConfig = (): SidekickConfig => {
  const context = useContext(SidekickConfigContext);
  const { i18n } = useTranslation(SIDEKICK_TRANSLATION_NAMESPACE);

  return useMemo<SidekickConfig>(() => {
    if (context !== null) {
      return context;
    }
    return {
      timeZone: resolveBrowserTimeZone(),
      locale: i18n.language || 'en-US',
    };
  }, [context, i18n.language]);
};
